import { useCallback, useEffect, useRef, useState } from "react";
import { Burst } from "../components/Burst";
import { Combo } from "../components/Combo";
import { Keypad } from "../components/Keypad";
import { Modal } from "../components/Modal";
import { QuestionView } from "../components/QuestionView";
import { TrialProgress } from "../components/TrialProgress";
import type { StripeMarks } from "../content/trials";
import { comboOf, comboTier, isTierStep } from "../game/combo";
import { solvedText, spokenText } from "../game/questions/question";
import type { RoundResult } from "../game/results";
import { canDiscard, formatClock, LOW_TIME_SECONDS, type RoundContext } from "../game/round";
import { useKeyboard } from "../hooks/useKeyboard";
import { useRound } from "../hooks/useRound";
import { speak, stopSpeaking } from "../speech/speech";
import "./Play.css";

interface Props {
  /** Read once, when the round mounts. */
  context: RoundContext;
  countdown: boolean;
  /** Shown in place of the clock in an untimed round, e.g. "Practice". */
  label: string;
  /** "stop" throws the round away (trials); "done" ends and keeps it (practice). */
  end: "stop" | "done";
  audio: boolean;
  /** A trial's pass mark and stripes, for the progress bar. */
  target?: { passMark: number; stripes: StripeMarks; seconds: number } | undefined;
  /** Hide the trial progress bar (a parent setting). The score still shows. */
  bar?: boolean;
  /** The colour of the rank in play, for the top combo tier. */
  rankColour?: string | undefined;
  onFinished: (round: RoundResult) => void;
  onStopped: () => void;
}

export function Play({
  context,
  countdown,
  label,
  end,
  audio,
  target,
  bar = true,
  rankColour,
  onFinished,
  onStopped,
}: Props) {
  const { state, seconds, press, stop, finish, pass } = useRound(context, countdown);
  const live = state.phase === "live" || state.phase === "feedback";
  const [confirming, setConfirming] = useState(false);
  const keepGoing = useRef<HTMLButtonElement>(null);

  useKeyboard(press, live && !confirming, pass);

  // In a trial, Stop throws the run away only at the very start. After that
  // it asks, and ending keeps the score (it counts as an attempt and for stripes).
  const onStop = useCallback(() => {
    if (canDiscard(state, Date.now())) stop();
    else setConfirming(true);
  }, [state, stop]);
  const closeConfirm = useCallback(() => setConfirming(false), []);

  // How long the round really lasted (untimed practice has no clock).
  const startedAt = useRef<number | null>(null);
  useEffect(() => {
    if (live && startedAt.current === null) startedAt.current = Date.now();
  }, [live]);

  // Hand the finished or abandoned round to the app once, after render.
  useEffect(() => {
    if (state.phase === "finished")
      onFinished({
        score: state.score,
        tried: state.tried,
        log: state.log,
        durationMs: startedAt.current === null ? null : Date.now() - startedAt.current,
      });
    else if (state.phase === "abandoned") onStopped();
  }, [state, onFinished, onStopped]);

  // Read each new question aloud; stop talking when the round ends.
  const question = live ? state.question : null;
  useEffect(() => {
    if (audio && question) speak(spokenText(question.tokens));
  }, [audio, question]);
  useEffect(() => stopSpeaking, []);
  const hear = useCallback(() => {
    if (question) speak(spokenText(question.tokens));
  }, [question]);

  if (state.phase === "countdown") {
    if (state.count === 0) return <section className="screen play" />;
    return (
      <section className="screen play play-countdown" aria-live="assertive">
        <div className="count display" key={state.count}>
          {state.count}
        </div>
        {target && <p className="count-tip">Wrong answers don&apos;t lose points. Keep going!</p>}
      </section>
    );
  }
  if (!live) return null;

  const fb = state.feedback;
  const low = seconds !== null && seconds <= LOW_TIME_SECONDS;
  const wrongSoFar = state.tried - state.score;
  const firstWrong = !!target && fb?.kind === "wrong" && wrongSoFar === 1;
  // Combos are read off the log, so they outlive the 180 ms feedback flash.
  const combo = comboOf(state.log);
  const tier = comboTier(combo);
  const burst = isTierStep(combo) && tier && (tier.sparkles > 0 || tier.flash) ? tier : null;
  const plus = state.log.at(-1)?.correct ? state.log.length : 0;
  return (
    <section className="screen play">
      <div className="hud">
        {seconds !== null ? (
          <div className={"timer display" + (low ? " low" : "")} aria-live="off">
            {formatClock(seconds)}
          </div>
        ) : (
          <div className="hud-label display">{label}</div>
        )}
        <div className="hud-mid">
          <div key={state.score} className={"score" + (state.score ? " bump" : "")}>
            {state.score} correct
          </div>
          <Combo count={combo} rankColour={rankColour} />
        </div>
        {end === "stop" ? (
          <button type="button" className="ghost" onClick={onStop}>
            Stop
          </button>
        ) : (
          <button type="button" className="mini done" onClick={finish}>
            Done
          </button>
        )}
      </div>
      {target && bar && seconds !== null && (
        <TrialProgress
          score={state.score}
          passMark={target.passMark}
          stripes={target.stripes}
          seconds={target.seconds}
          secondsLeft={seconds}
        />
      )}
      <div className="arena">
        <div className="question-block">
          <div className="qwrap">
            <QuestionView
              question={state.question}
              input={state.input}
              feedback={fb}
              onHear={audio ? hear : undefined}
            />
            {/* Keyed to restart on each answer; the keys must differ from each other. */}
            {plus > 0 && (
              <span key={`plus-${plus}`} className="plus-one display" aria-hidden="true">
                +1
              </span>
            )}
            {burst && (
              <Burst key={`burst-${state.log.length}`} tier={burst} rankColour={rankColour} />
            )}
          </div>
          <div className="fix" aria-live="polite">
            {fb?.kind === "wrong" || fb?.kind === "passed" ? solvedText(fb.question) : ""}
            {firstWrong && <span className="no-loss"> No points lost!</span>}
          </div>
          <button
            type="button"
            className="ghost pass-btn"
            onClick={pass}
            disabled={state.phase !== "live" || confirming}
          >
            Pass
          </button>
        </div>
        <Keypad onPress={press} disabled={state.phase !== "live" || confirming} />
      </div>
      <Modal
        open={confirming}
        label="End the trial?"
        onClose={closeConfirm}
        initialFocus={keepGoing}
      >
        <div className="panel welcome">
          <h2 className="display">End the trial?</h2>
          <p>Your score still counts for stripes. The clock keeps going while you decide.</p>
          <button ref={keepGoing} type="button" className="big display" onClick={closeConfirm}>
            Keep going
          </button>
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setConfirming(false);
              finish();
            }}
          >
            End trial
          </button>
        </div>
      </Modal>
    </section>
  );
}
